import type {FC} from 'react';
import {useCallback} from 'react';
import {useNavigate} from 'react-router';

import {useI18n} from '@yourcoach/i18n';
import {authStore} from '@yourcoach/shared/api/auth';
import LogoutIcon from '@yourcoach/shared/assets/icons/secondary/Logout.svg';
import ProfileIcon from '@yourcoach/shared/assets/icons/secondary/Profile.svg';
import {alert} from '@yourcoach/shared/components/alert';

import Loader from '@src/components/Loader/Loader';
import localAppStore from '@src/context/appStore';
import useIsVisible from '@src/hooks/useIsVisible';
import {routerPath} from '@src/routes/path';
import {ContextMenu} from '@src/v2/components/ContextMenu';

import {getBrowserPath} from '../../../utils/getBrowserPath';
import * as S from './styles';

interface Props {
  menuId: string;
}

export const ProfileContextMenu: FC<Props> = ({menuId}) => {
  const {t} = useI18n();

  const navigate = useNavigate();

  const [overlayIsVisible, showOverlay, hideOverlay] = useIsVisible();

  const onLogoutButtonClick = useCallback(async () => {
    try {
      showOverlay();

      await authStore.logout();

      navigate(routerPath.login.$buildPath({}), {
        replace: true,
        state: {from: getBrowserPath()},
      });

      localAppStore.setIsAuth(false);
    } catch (error) {
      alert.error(error);

      hideOverlay();
    }
  }, [hideOverlay, navigate, showOverlay]);

  const onProfileButtonClick = useCallback(() => {
    navigate(routerPath.profile.$buildPath({}));
  }, [navigate]);

  return (
    <>
      <ContextMenu.Menu animation={false} id={menuId}>
        <ContextMenu.Item onClick={onProfileButtonClick} variant={'success'}>
          <S.Label>{t('web-app:profile_context_menu.profile_button')}</S.Label>
          <ProfileIcon />
        </ContextMenu.Item>
        <ContextMenu.Item onClick={onLogoutButtonClick} variant={'success'}>
          <S.Label>{t('web-app:profile_context_menu.logout_button')}</S.Label>
          <LogoutIcon />
        </ContextMenu.Item>
      </ContextMenu.Menu>
      {overlayIsVisible ? (
        <S.Overlay>
          <Loader />
        </S.Overlay>
      ) : null}
    </>
  );
};
